// src/pages/TherapistDashboard.tsx

import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import TherapistBridgeTimeline from '../components/bridge/TherapistBridgeTimeline';
import PatientWindow from '../components/bridge/PatientWindow';
import useHomeSignals from '../hooks/useHomeSignals';
import {
  Users,
  Send,
  X,
  UserPlus,
  Lock,
  ArrowRight,
  Activity,
  Brain,
  Zap,
  Palette,
  Search,
  CheckCircle,
  Copy,
  Check,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import type { Profile } from '../hooks/useAppData';
import { getEngagementRisk } from '../services/ai/aiClient';
import { postApi } from '../services/apiFetch';
import type {
  EngagementRiskInput,
  EngagementRiskOutput,
  SessionToMissionsOutput,
  SuggestedMissionDraft,
} from '../services/ai/aiTypes';

// מיפוי אווטארים
import AIImpactMetrics from '../components/AIImpactMetrics';
import TherapistAggregateInsights from '../components/TherapistAggregateInsights';
import { useAppDataContext } from '../context/AppDataContext';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../utils/errors';
import Critter from '../components/glyphs/Critter';
import Glyph from '../components/glyphs/Glyph';

const getAgeGroup = (age?: number): string | undefined => {
  if (typeof age !== 'number') return undefined;
  if (age < 13) return 'child';
  if (age < 18) return 'teen';
  return 'young_adult';
};

// פונקציית עזר לקביעת צבע לפי אחוזי הסיכון
const getRiskColor = (percentage: number) => {
  if (percentage >= 80) return 'text-red-500 border-red-500/50 bg-red-500/10';
  if (percentage >= 50) return 'text-orange-500 border-orange-500/50 bg-orange-500/10';
  if (percentage >= 30) return 'text-yellow-400 border-yellow-400/50 bg-yellow-400/10';
  return 'text-emerald-400 border-emerald-400/50 bg-emerald-400/10';
};

const TherapistDashboard: React.FC = () => {
  const { subscription } = useAppDataContext();
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');
  const [viewingPatient, setViewingPatient] = useState<Profile | null>(null);
  const [patientStats, setPatientStats] = useState<{ day: string; mood: number }[]>([]);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [newPatient, setNewPatient] = useState({ name: '', age: '', issue: '' });
  const [lastCreatedCreds, setLastCreatedCreds] = useState<{
    username: string;
    password: string;
  } | null>(null);
  // The password exists only on this screen, so closing it takes a second, deliberate step.
  const [credsStep, setCredsStep] = useState<'show' | 'confirm'>('show');
  const [credsCopied, setCredsCopied] = useState(false);
  const [missionModalOpen, setMissionModalOpen] = useState(false);
  const [missionTitle, setMissionTitle] = useState('');
  const [missionDesc, setMissionDesc] = useState('');
  const [missionXP, setMissionXP] = useState(50);
  const [missionType, setMissionType] = useState<'active' | 'mindful' | 'creative'>('active');
  const [missionRationale, setMissionRationale] = useState('');
  const [engagementRisk, setEngagementRisk] = useState<EngagementRiskOutput | null>(null);
  const [engagementRiskLoading, setEngagementRiskLoading] = useState(false);
  const [engagementRiskError, setEngagementRiskError] = useState<string | null>(null);
  const [sessionAiModalOpen, setSessionAiModalOpen] = useState(false);
  const [sessionSummary, setSessionSummary] = useState('');
  const [sessionAiLoading, setSessionAiLoading] = useState(false);
  const [sessionAiError, setSessionAiError] = useState<string | null>(null);
  const [sessionAiResult, setSessionAiResult] = useState<SessionToMissionsOutput | null>(null);
  // Stage B / 10x instrumentation: timestamp of the AI draft this batch came from,
  // used to compute "Time-to-Plan" (draft generated -> mission approved) per mission.
  const [draftGeneratedAt, setDraftGeneratedAt] = useState<string | null>(null);

  // State החדש ל-Combo Boxes
  const [focusArea, setFocusArea] = useState('כללי');
  const [difficultyLevel, setDifficultyLevel] = useState('בינוני');
  const [weeklyMissions, setWeeklyMissions] = useState(3);
  const [maxMinutes, setMaxMinutes] = useState(10);

  const [editableMissions, setEditableMissions] = useState<
    (SuggestedMissionDraft & { dayOfWeek?: string; order?: number })[]
  >([]);
  const [editingMissionId, setEditingMissionId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    title: string;
    description: string;
    effort: number;
  }>({ title: '', description: '', effort: 15 });

  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchPatients();
    const interval = setInterval(fetchPatients, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchPatients = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'patient')
        .eq('therapist_id', user.id)
        .order('last_seen', { ascending: false });

      if (error) throw error;
      if (data) {
        setPatients(data as Profile[]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching patients:', err);
      setLoading(false);
    }
  };

  const homeSignals = useHomeSignals(patients.map((p) => p.id));

  const filteredPatients = patients.filter(
    (patient) =>
      patient.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.username?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const fetchPatientStats = async (patientId: string) => {
    const { data } = await supabase
      .from('mood_logs')
      .select('created_at, mood_score')
      .eq('user_id', patientId)
      .order('created_at', { ascending: false })
      .limit(7);

    if (data && data.length > 0) {
      const formattedData = data.reverse().map((log) => ({
        day: new Date(log.created_at).toLocaleDateString('he-IL', { weekday: 'short' }),
        mood: log.mood_score,
      }));
      setPatientStats(formattedData);
    } else {
      setPatientStats([]);
    }
  };

  const copyCreds = async () => {
    if (!lastCreatedCreds) return;
    const text = `username: ${lastCreatedCreds.username}
password: ${lastCreatedCreds.password}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API blocked (e.g. insecure context): fall back to a hidden textarea.
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(area);
      }
    }
    setCredsCopied(true);
  };

  const closeCreds = () => {
    setLastCreatedCreds(null);
    setCredsStep('show');
    setCredsCopied(false);
  };

  // Refreshing or leaving while the password is on screen would lose it for good.
  useEffect(() => {
    if (!lastCreatedCreds) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [lastCreatedCreds]);

  const handleCreatePatient = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const currentPlan = subscription?.plan_id || 'free';
    if (currentPlan === 'free' && patients.length >= 3) {
      alert('הגעת למכסת המטופלים במסלול החינמי. שדרג ל-Pro כדי להוסיף מטופלים ללא הגבלה.');
      setShowAddPatient(false);
      navigate('/therapist/billing');
      return;
    }

    try {
      setLoading(true);
      // The server creates the login and the profile; the browser can't create other users.
      const response = await postApi('/api/create-patient', {
        name: newPatient.name,
        age: newPatient.age,
        issue: newPatient.issue,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        alert(`שגיאה ביצירת המטופל: ${result.error || response.statusText}`);
        return;
      }

      fetchPatients();
      setShowAddPatient(false);
      setNewPatient({ name: '', age: '', issue: '' });
      setCredsStep('show');
      setCredsCopied(false);
      setLastCreatedCreds({ username: result.username, password: result.password });
    } catch (err) {
      console.error(err);
      alert('שגיאה ביצירת המטופל.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignMission = async (e: React.FormEvent) => {
    e.preventDefault();
    const targetPatient = viewingPatient;
    if (!targetPatient) return;

    try {
      const { error } = await supabase.from('missions').insert({
        user_id: targetPatient.id,
        title: missionTitle,
        description: missionDesc,
        xp_reward: missionXP,
        type: missionType,
        rationale: missionRationale || 'הוקצה על ידי המטפל.',
        is_completed: false,
      });

      if (error) throw error;

      showSuccessNotification(`המשימה נשלחה בהצלחה ל${targetPatient.full_name}!`);
      setMissionModalOpen(false);
      setMissionTitle('');
      setMissionDesc('');
      setMissionRationale('');
      setMissionXP(50);
      setMissionType('active');
    } catch (err) {
      alert('שגיאה בשליחת המשימה');
    }
  };

  const showSuccessNotification = (message: string) => {
    setSuccessMessage(message);
    setShowSuccessToast(true);
    setTimeout(() => {
      setShowSuccessToast(false);
    }, 3000);
  };

  const loadEngagementRisk = async (patient: Profile) => {
    try {
      setEngagementRiskLoading(true);
      setEngagementRiskError(null);
      setEngagementRisk(null);

      const now = new Date();
      const nowIso = now.toISOString();
      const last7Start = new Date(now);
      last7Start.setDate(now.getDate() - 7);
      const prev7Start = new Date(now);
      prev7Start.setDate(now.getDate() - 14);
      const prev7End = new Date(now);
      prev7End.setDate(now.getDate() - 7);

      let daysSinceLastLogin = 999;
      if (patient.last_seen) {
        const lastSeen = new Date(patient.last_seen);
        const lastSeenMid = new Date(
          lastSeen.getFullYear(),
          lastSeen.getMonth(),
          lastSeen.getDate(),
        ).getTime();
        const todayMid = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
        ).getTime();
        const diffMs = todayMid - lastSeenMid;
        daysSinceLastLogin = Math.max(0, Math.round(diffMs / (1000 * 60 * 60 * 24)));
      }

      const { data: missionsLast7 } = await supabase
        .from('missions')
        .select('id, is_completed, created_at')
        .eq('user_id', patient.id)
        .gte('created_at', last7Start.toISOString())
        .lte('created_at', nowIso);

      const missionsAssignedLast7 = missionsLast7?.length || 0;
      const missionsCompletedLast7 =
        missionsLast7?.filter((m) => m.is_completed)?.length || 0;

      const { data: moodLast7 } = await supabase
        .from('mood_logs')
        .select('id, created_at')
        .eq('user_id', patient.id)
        .gte('created_at', last7Start.toISOString())
        .lte('created_at', nowIso);

      const moodLogsLast7 = moodLast7?.length || 0;

      const { data: moodPrev7 } = await supabase
        .from('mood_logs')
        .select('id, created_at')
        .eq('user_id', patient.id)
        .gte('created_at', prev7Start.toISOString())
        .lt('created_at', prev7End.toISOString());

      const moodLogsPrev7 = moodPrev7?.length || 0;

      const ageGroup = getAgeGroup(patient.age);

      // תיקון מצבי קצה בחוסר נתונים עבור סיכון נטישה
      if (missionsAssignedLast7 === 0 && moodLogsLast7 === 0 && moodLogsPrev7 === 0 && (patient.streak || 0) === 0) {
        setEngagementRiskError('אין מספיק היסטוריית פעילות (משימות/מצב רוח/רצף) בשבועיים האחרונים כדי לבסס הערכת סיכון מדויקת.');
        setEngagementRiskLoading(false);
        return;
      }

      // --- שלב התיקון: שליפת סיכומי המפגשים ---
      const { data: recentSummariesData } = await supabase
        .from('patient_summaries')
        .select('content')
        .eq('patient_id', patient.id)
        .order('created_at', { ascending: false })
        .limit(2);

      const summariesText = recentSummariesData && recentSummariesData.length > 0
        ? recentSummariesData.map(s => s.content).join(" | ")
        : "אין סיכומים קודמים זמינים.";

      // --- בניית ה-Payload עם session_context ---
      const riskPayload: EngagementRiskInput = {
        task_type: 'ENGAGEMENT_RISK',
        locale: 'he-IL',
        patient_profile: {
          age_group: ageGroup,
          treatment_stage: 'middle',
          session_context: summariesText // הזרקת סיכומי המפגשים
        },
        engagement_data: {
          days_since_last_login: daysSinceLastLogin,
          missions_assigned_last_7_days: missionsAssignedLast7,
          missions_completed_last_7_days: missionsCompletedLast7,
          mood_logs_last_7_days: moodLogsLast7,
          mood_logs_previous_7_days: moodLogsPrev7,
          streak_current_days: patient.streak || 0,
          streak_best_days: patient.streak || 0,
        },
      };

      const result = await getEngagementRisk(riskPayload);
      setEngagementRisk(result);
    } catch (err) {
      console.error('Engagement risk error:', err);
      // שיפור הודעת השגיאה - UI Polish
      setEngagementRiskError('שירות הערכת הסיכון אינו זמין כרגע. נסה שוב מאוחר יותר.');
    } finally {
      setEngagementRiskLoading(false);
    }
  };

  const handleSessionAiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewingPatient) return;

    try {
      setSessionAiLoading(true);
      setSessionAiError(null);
      setSessionAiResult(null);
      setDraftGeneratedAt(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const summaryTitle = `סיכום מפגש - ${new Date().toLocaleDateString('he-IL')}`;

      const { error: saveError } = await supabase.from('patient_summaries').insert({
        patient_id: viewingPatient.id,
        therapist_id: user.id,
        title: summaryTitle,
        content: sessionSummary,
      });

      if (saveError) {
        throw new Error('שגיאה בשמירת הסיכום: ' + saveError.message);
      }

      const ageGroup = getAgeGroup(viewingPatient.age);

      // שליחה ישירה ל-API במקום דרך getSessionMissionsDraft
      const response = await postApi('/api/ai-session-to-missions', {
        session_summary: sessionSummary,
        therapy_goals: ['חיזוק התמדה בתהליך הטיפולי', 'בהתאם לסיכום המפגש'],
        constraints: {
          max_missions: weeklyMissions,
          max_effort_minutes_per_mission: maxMinutes,
        },
        patient_profile: {
          age_group: ageGroup,
          interests: [],
          sensitivity_notes: viewingPatient.issue_description,
        },
        focus_area: focusArea,
        difficulty_level: difficultyLevel,
        weekly_missions: weeklyMissions,
        max_minutes: maxMinutes,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate missions');
      }

      const result = await response.json();
      setSessionAiResult(result);

      // Stage B / 10x instrumentation: record the draft-generation event so we can
      // later compute Plan Adoption Rate (approved / suggested) and Time-to-Plan.
      const generatedAt = new Date().toISOString();
      setDraftGeneratedAt(generatedAt);
      if (viewingPatient) {
        supabase.auth.getUser().then(({ data: { user } }) => {
          if (!user) return;
          supabase.from('ai_session_drafts').insert({
            therapist_id: user.id,
            patient_id: viewingPatient.id,
            suggested_count: result.suggested_missions?.length || 0,
            generated_at: generatedAt,
          }).then(({ error }) => {
            if (error) console.error('Failed to log AI draft event:', error);
          });
        });
      }

      const missionsWithDayAndOrder = result.suggested_missions.map((m: SuggestedMissionDraft, idx: number) => ({
        ...m,
        id: m.id || `mission-${Date.now()}-${idx}`,
        dayOfWeek: undefined,
        order: idx,
      }));
      setEditableMissions(missionsWithDayAndOrder);
    } catch (err) {
      console.error('Session process error:', err);
      setSessionAiError(getErrorMessage(err) || 'שגיאה בתהליך השמירה או העיבוד.');
    } finally {
      setSessionAiLoading(false);
    }
  };

  const deleteSuggestedMission = (e: React.MouseEvent, missionId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (window.confirm('האם למחוק משימה זו?')) {
      setEditableMissions((prev) => prev.filter((m) => m.id !== missionId));
      showSuccessNotification('המשימה נמחקה בהצלחה');
    }
  };

  const startEditMission = (mission: SuggestedMissionDraft & { dayOfWeek?: string; order?: number }) => {
    setEditingMissionId(mission.id);
    setEditForm({
      title: mission.title,
      description: mission.message_to_patient_draft,
      effort: mission.estimated_effort_minutes || 15,
    });
  };

  const saveEditMission = () => {
    setEditableMissions((prev) =>
      prev.map((m) =>
        m.id === editingMissionId
          ? {
            ...m,
            title: editForm.title,
            message_to_patient_draft: editForm.description,
            estimated_effort_minutes: editForm.effort,
          }
          : m
      )
    );
    setEditingMissionId(null);
    showSuccessNotification('השינויים נשמרו');
  };

  const cancelEditMission = () => {
    setEditingMissionId(null);
  };

  const changeMissionDay = (missionId: string, day: string) => {
    setEditableMissions((prev) =>
      prev.map((m) => (m.id === missionId ? { ...m, dayOfWeek: day } : m))
    );
  };

  const moveMissionUp = (missionId: string) => {
    setEditableMissions((prev) => {
      const idx = prev.findIndex((m) => m.id === missionId);
      if (idx <= 0) return prev;
      const newList = [...prev];
      [newList[idx - 1], newList[idx]] = [newList[idx], newList[idx - 1]];
      return newList.map((m, i) => ({ ...m, order: i }));
    });
  };

  const moveMissionDown = (missionId: string) => {
    setEditableMissions((prev) => {
      const idx = prev.findIndex((m) => m.id === missionId);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const newList = [...prev];
      [newList[idx], newList[idx + 1]] = [newList[idx + 1], newList[idx]];
      return newList.map((m, i) => ({ ...m, order: i }));
    });
  };

  const approveSuggestedMission = async (m: SuggestedMissionDraft & { dayOfWeek?: string }) => {
    if (!viewingPatient) return;
    try {
      const base = m.estimated_effort_minutes || 15;
      const xp = Math.min(200, Math.max(30, base * 5));

      const { error } = await supabase.from('missions').insert({
        user_id: viewingPatient.id,
        title: m.title,
        description: m.message_to_patient_draft,
        xp_reward: xp,
        type: 'mindful',
        rationale:
          m.description_for_therapist ||
          'משימה הוצעה ע״י מודל AI ואושרה על ידי המטפל.',
        is_completed: false,
        scheduled_day: m.dayOfWeek,
        // Stage B / 10x instrumentation: lets us compute Time-to-Plan
        // (draft_generated_at -> approved_at) and Plan Adoption Rate.
        ai_generated: true,
        draft_generated_at: draftGeneratedAt,
        approved_at: new Date().toISOString(),
      });

      if (error) throw error;

      showSuccessNotification(`המשימה "${m.title}" נשלחה למטופל ביום ${m.dayOfWeek}!`);
      setEditableMissions((prev) => prev.filter((mission) => mission.id !== m.id));

      if (editableMissions.length === 1) {
        setTimeout(() => {
          setSessionAiModalOpen(false);
          setSessionAiResult(null);
      setDraftGeneratedAt(null);
          setEditableMissions([]);
          showSuccessNotification('כל המשימות נשלחו בהצלחה!');
        }, 500);
      }
    } catch (err) {
      console.error(err);
      alert('שגיאה ביצירת המשימה. נסה שוב.');
    }
  };

  const openPatientDetail = (patient: Profile) => {
    setViewingPatient(patient);
    setViewMode('detail');
    setPatientStats([]);
    setEngagementRisk(null);
    setEngagementRiskError(null);
    setSessionAiResult(null);
      setDraftGeneratedAt(null);
    setSessionAiError(null);
    setEditableMissions([]);
    fetchPatientStats(patient.id);
    loadEngagementRisk(patient);
  };

  const closePatientDetail = () => {
    setViewingPatient(null);
    setViewMode('list');
    setPatientStats([]);
    setEngagementRisk(null);
    setEngagementRiskError(null);
    setSessionAiResult(null);
      setDraftGeneratedAt(null);
    setSessionAiError(null);
    setSessionAiModalOpen(false);
    setMissionModalOpen(false);
    setEditableMissions([]);
  };

  if (viewMode === 'detail' && viewingPatient) {
    return (
      <div className="h-full flex flex-col bg-background overflow-y-auto pb-20 dir-rtl scrollbar-hide transaction-colors duration-300">
        {showSuccessToast && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100] animate-slideDown">
            <div className="bg-primary text-primary-foreground px-6 py-3 rounded-xl shadow-2xl flex items-center gap-2">
              <CheckCircle size={20} />
              <span className="font-bold">{successMessage}</span>
            </div>
          </div>
        )}

        <div className="p-4 border-b border-border flex items-center gap-4 sticky top-0 bg-background/90 backdrop-blur z-10">
          <button
            onClick={closePatientDetail}
            className="p-2 bg-muted rounded-full hover:bg-accent"
          >
            <ArrowRight size={20} className="text-foreground" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-foreground">{viewingPatient.full_name}</h2>
            <p className="text-xs text-muted-foreground">@{viewingPatient.username} | {viewingPatient.age} שנים</p>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex gap-4">
            <div className="w-16 h-16 bg-bridge-soft rounded-2xl flex items-center justify-center text-4xl border-2 border-bridge shrink-0">
              <Critter id={viewingPatient.avatar_id} size={52} />
            </div>
            <div className="flex-1 bg-card p-3 rounded-2xl border border-border">
              <span className="text-xs text-muted-foreground">בעיה מדווחת</span>
              <div className="text-sm text-foreground">
                {viewingPatient.issue_description || 'לא דווח'}
              </div>
            </div>
          </div>

          <TherapistBridgeTimeline patientId={viewingPatient.id} patientName={viewingPatient.full_name} />

          <div className="flex gap-3">
            <button
              onClick={() => setMissionModalOpen(true)}
              className="flex-1 bg-primary hover:bg-primary/90 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
            >
              <Send size={18} /> משימה ידנית חדשה
            </button>
            <button
              onClick={() => setSessionAiModalOpen(true)}
              className="flex-1 bg-muted hover:bg-muted/80 text-foreground py-3 rounded-xl font-bold flex items-center justify-center gap-2 border border-border"
            >
              <Brain size={18} /> סיכום + משימות
            </button>
          </div>

          <div className="grid gap-6 md:grid-cols-2 md:items-start">
          <div className="space-y-3">
            <h3 className="text-foreground font-bold flex items-center gap-2">
              <Brain size={18} className="text-primary" /> סיכון נטישה (AI)
            </h3>
            <div className="bg-card p-4 rounded-xl border border-border space-y-3">
              {engagementRiskLoading && (
                <p className="text-xs text-muted-foreground">
                  מחשב הערכת סיכון לפי דפוסי שימוש...
                </p>
              )}

              {engagementRiskError && (
                <p className="text-xs text-red-400">{engagementRiskError}</p>
              )}

              {engagementRisk && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-muted-foreground">הסתברות לנטישה</span>
                      <span className={`text-3xl font-bold px-4 py-2 rounded-xl border w-fit ${getRiskColor(engagementRisk.churn_percentage)}`}>
                        {engagementRisk.churn_percentage}%
                      </span>
                    </div>

                    {/* בר ויזואלי של האחוזים */}
                    <div className="flex-1 mx-6 h-4 bg-muted rounded-full overflow-hidden border border-border relative">
                      <div
                        className={`h-full transition-all duration-1000 ease-out rounded-full ${engagementRisk.churn_percentage >= 50 ? 'bg-red-500' :
                          engagementRisk.churn_percentage >= 30 ? 'bg-yellow-500' : 'bg-emerald-500'
                          }`}
                        style={{ width: `${engagementRisk.churn_percentage}%` }}
                      />
                    </div>
                  </div>

                  {engagementRisk.key_signals?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-muted-foreground mb-2 font-bold flex items-center gap-1.5"><Glyph name="flag" size={16} /> נורות אזהרה</p>
                      <ul className="grid gap-2">
                        {engagementRisk.key_signals.map((s, idx) => (
                          <li key={idx} className="text-xs text-foreground bg-muted/50 p-2 rounded-lg border border-border/50 flex items-start gap-2">
                            <span className="text-red-400 mt-0.5">•</span> {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {engagementRisk.recommended_actions_for_therapist?.length > 0 && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-2 font-bold flex items-center gap-1.5"><Glyph name="bulb" size={16} /> פעולות מומלצות</p>
                      <ul className="grid gap-2">
                        {engagementRisk.recommended_actions_for_therapist.map(
                          (s, idx) => (
                            <li key={idx} className="text-xs text-foreground bg-primary/10 p-2 rounded-lg border border-primary/20 flex items-start gap-2">
                              <Glyph name="check" size={14} className="mt-0.5 shrink-0" /> {s}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}
                </>
              )}

              {!engagementRiskLoading &&
                !engagementRisk &&
                !engagementRiskError && (
                  <p className="text-xs text-muted-foreground">
                    אין עדיין נתוני שימוש מספיקים כדי לחשב סיכון בצורה משמעותית.
                  </p>
                )}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-foreground font-bold flex items-center gap-2">
              <Activity size={18} className="text-green-400" /> דופק רגשי (אמת)
            </h3>

            <div className="bg-card p-4 rounded-xl border border-border h-64">
              <h4 className="text-xs text-muted-foreground mb-2">
                מצב רוח מדווח (5=שמשי, 1=סוער)
              </h4>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={
                    patientStats.length > 0
                      ? patientStats
                      : [{ day: 'אין נתונים', mood: 0 }]
                  }
                >
                  <defs>
                    <linearGradient id="colorMood" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#d98a1f" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#d98a1f" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} domain={[0, 6]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      borderColor: '#dde3ea',
                      borderRadius: '0.75rem',
                      color: '#1a2b42',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="mood"
                    stroke="#d98a1f"
                    fillOpacity={1}
                    fill="url(#colorMood)"
                  />
                </AreaChart>
              </ResponsiveContainer>
              {patientStats.length === 0 && (
                <p className="text-center text-xs text-muted-foreground mt-[-100px]">
                  אין עדיין דיווחים מהמטופל
                </p>
              )}
            </div>
          </div>
          </div>
        </div>

        {missionModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
            <div className="bg-card w-full max-w-sm border border-border rounded-2xl p-6 shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-foreground">
                  משימה ל{viewingPatient.full_name}
                </h3>
                <button
                  onClick={() => setMissionModalOpen(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleAssignMission} className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">סוג המשימה</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'active', icon: Zap, label: 'פיזי' },
                      { id: 'mindful', icon: Brain, label: 'מנטלי' },
                      { id: 'creative', icon: Palette, label: 'יצירה' },
                    ].map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setMissionType(t.id as typeof missionType)}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold flex flex-col items-center gap-1 border ${missionType === t.id
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'bg-muted border-border text-muted-foreground'
                          }`}
                      >
                        <t.icon size={16} /> {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">כותרת</label>
                  <input
                    value={missionTitle}
                    onChange={(e) => setMissionTitle(e.target.value)}
                    className="w-full bg-muted border border-border rounded-lg p-3 text-foreground focus:border-primary outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">תיאור</label>
                  <textarea
                    value={missionDesc}
                    onChange={(e) => setMissionDesc(e.target.value)}
                    className="w-full bg-muted border border-border rounded-lg p-3 text-foreground focus:border-primary outline-none h-16 resize-none scrollbar-hide"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">XP</label>
                  <input
                    type="number"
                    min={10}
                    max={500}
                    step={10}
                    value={missionXP}
                    onChange={(e) => setMissionXP(parseInt(e.target.value, 10) || 0)}
                    className="w-full bg-muted border border-border rounded-lg p-2 text-foreground focus:border-primary outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs text-muted-foreground">היגיון טיפולי (לא חובה)</label>
                  <textarea
                    value={missionRationale}
                    onChange={(e) => setMissionRationale(e.target.value)}
                    className="w-full bg-muted border border-border rounded-lg p-2 text-foreground focus:border-primary outline-none h-16 resize-none text-xs scrollbar-hide"
                    placeholder="איך המשימה אמורה לעזור למטופל?"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 mt-2"
                >
                  <Send size={18} /> שלח
                </button>
              </form>
            </div>
          </div>
        )}

        {sessionAiModalOpen && (
          <div className="absolute inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 overflow-y-auto scrollbar-hide">
            <div className="bg-card w-full max-w-lg border border-border rounded-3xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto my-8 scrollbar-hide">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <Brain size={20} /> טיוטת משימות לסשן הנוכחי
                </h3>
                <button
                  onClick={() => {
                    setSessionAiModalOpen(false);
                    setSessionAiResult(null);
      setDraftGeneratedAt(null);
                    setSessionAiError(null);
                    setEditableMissions([]);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSessionAiSubmit} className="space-y-3 mb-4">
                {/* COMBO-1 & COMBO-2 */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground h-8 flex items-end gap-1.5 pb-1">
                      <Glyph name="target" size={16} /> פוקוס השבוע
                    </label>
                    <select
                      value={focusArea}
                      onChange={(e) => setFocusArea(e.target.value)}
                      className="w-full bg-muted border border-border rounded-2xl p-2 h-12 text-foreground text-xs focus:border-primary outline-none"
                    >
                      <option value="כללי">כללי</option>
                      <option value="עבודה קוגניטיבית">עבודה קוגניטיבית</option>
                      <option value="פעילות התנהגותית">פעילות התנהגותית</option>
                      <option value="הרפיה ומיינדפולנס">הרפיה ומיינדפולנס</option>
                      <option value="תקשורת חברתית">תקשורת חברתית</option>
                      <option value="השגת יעדים קטנים">השגת יעדים קטנים</option>
                      <option value="בניית הרגלים">בניית הרגלים</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground h-8 flex items-end gap-1.5 pb-1">
                      <Glyph name="bolt" size={16} /> רמת אתגר
                    </label>
                    <select
                      value={difficultyLevel}
                      onChange={(e) => setDifficultyLevel(e.target.value)}
                      className="w-full bg-muted border border-border rounded-2xl p-2 h-12 text-foreground text-xs focus:border-primary outline-none"
                    >
                      <option value="קל">קל</option>
                      <option value="בינוני">בינוני</option>
                      <option value="קשה">קשה</option>
                    </select>
                  </div>
                </div>

                {/* סיכום - אופציונלי */}
                <div>
                  <label className="text-xs text-muted-foreground flex items-center gap-1.5"><Glyph name="note" size={16} /> סיכום הפגישה (אופציונלי)</label>
                  <textarea
                    value={sessionSummary}
                    onChange={(e) => setSessionSummary(e.target.value)}
                    className="w-full bg-muted border border-border rounded-2xl p-3 text-foreground focus:border-primary outline-none resize-none text-sm leading-relaxed scrollbar-hide"
                    rows={sessionSummary ? 8 : 3}
                    placeholder="הקלד כאן סיכום של הפגישה..."
                  />
                </div>

                {/* COMBO-3 & COMBO-4 */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground h-8 flex items-end gap-1.5 pb-1"><Glyph name="chart" size={16} /> כמות משימות</label>
                    <select
                      value={weeklyMissions}
                      onChange={(e) => setWeeklyMissions(Number(e.target.value))}
                      className="w-full bg-muted border border-border rounded-2xl p-2 h-12 text-foreground text-xs focus:border-primary outline-none"
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground h-8 flex items-end pb-1">
                      ⏱️ זמן מקסימלי (דקות)
                    </label>
                    <select
                      value={maxMinutes}
                      onChange={(e) => setMaxMinutes(Number(e.target.value))}
                      className="w-full bg-muted border border-border rounded-2xl p-2 h-12 text-foreground text-xs focus:border-primary outline-none"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                    </select>
                  </div>
                </div>

                {sessionAiError && (
                  <p className="text-xs text-red-400">{sessionAiError}</p>
                )}

                <button
                  type="submit"
                  disabled={sessionAiLoading}
                  className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2 text-sm"
                >
                  {sessionAiLoading ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      מעבד סיכום...
                    </>
                  ) : (
                    <>
                      <Brain size={16} /> שמור וצור טיוטת משימות
                    </>
                  )}
                </button>
              </form>

              {sessionAiResult && (
                <div className="border-t border-border pt-4 space-y-3">
                  <div className="bg-muted/60 rounded-lg p-3 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">
                      סיכום AI למטפל (לא נשלח למטופל):
                    </p>
                    <p className="text-xs text-foreground whitespace-pre-line">
                      {sessionAiResult.weekly_summary_for_therapist}
                    </p>
                  </div>

                  {sessionAiResult.suggested_goals_for_week?.length > 0 && (
                    <div className="bg-muted/40 rounded-lg p-3 border border-border">
                      <p className="text-xs text-muted-foreground mb-1">
                        מטרות מוצעות לשבוע:
                      </p>
                      <ul className="list-disc pr-4 space-y-1 text-xs text-foreground">
                        {sessionAiResult.suggested_goals_for_week.map((g, idx) => (
                          <li key={idx}>{g}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      משימות מוצעות ({editableMissions.length} נותרו):
                    </p>
                    {editableMissions.map((m) => (
                      <div
                        key={m.id}
                        className="bg-card rounded-xl border border-border p-4 space-y-3"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1">
                            {editingMissionId === m.id ? (
                              <input
                                value={editForm.title}
                                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                className="w-full bg-muted border border-border rounded-lg p-2 text-foreground text-sm"
                              />
                            ) : (
                              <h4 className="text-sm font-bold text-foreground">{m.title}</h4>
                            )}
                          </div>

                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => moveMissionUp(m.id)}
                              className="p-1 bg-muted hover:bg-accent rounded text-muted-foreground hover:text-foreground text-xs w-6 h-6 flex items-center justify-center"
                              title="הזז למעלה"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => moveMissionDown(m.id)}
                              className="p-1 bg-muted hover:bg-accent rounded text-muted-foreground hover:text-foreground text-xs w-6 h-6 flex items-center justify-center"
                              title="הזז למטה"
                            >
                              ↓
                            </button>

                            {editingMissionId === m.id ? (
                              <>
                                <button
                                  type="button"
                                  onClick={saveEditMission}
                                  className="p-1 rounded-full hover:bg-muted"
                                  aria-label="שמירה"
                                >
                                  <Glyph name="check" size={22} />
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditMission}
                                  className="p-1 rounded-full hover:bg-muted"
                                  aria-label="ביטול"
                                >
                                  <Glyph name="cross" size={22} />
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => startEditMission(m)}
                                className="p-1 rounded-full hover:bg-muted"
                                aria-label="עריכה"
                              >
                                <Glyph name="pencil" size={22} />
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={(e) => deleteSuggestedMission(e, m.id)}
                              className="p-1 rounded-full hover:bg-muted"
                              aria-label="מחיקה"
                            >
                              <Glyph name="trash" size={22} />
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">יום בשבוע:</label>
                          <select
                            value={m.dayOfWeek || ''}
                            onChange={(e) => changeMissionDay(m.id, e.target.value)}
                            className="w-full bg-muted border border-border rounded-lg p-2 text-foreground text-xs"
                          >
                            <option value="">בחר יום...</option>
                            <option value="ראשון">ראשון</option>
                            <option value="שני">שני</option>
                            <option value="שלישי">שלישי</option>
                            <option value="רביעי">רביעי</option>
                            <option value="חמישי">חמישי</option>
                            <option value="שישי">שישי</option>
                            <option value="שבת">שבת</option>
                          </select>
                        </div>

                        <div className="flex justify-between items-center">
                          <span className="text-xs text-muted-foreground">מאמץ משוער:</span>
                          {editingMissionId === m.id ? (
                            <input
                              type="number"
                              min={5}
                              max={60}
                              value={editForm.effort}
                              onChange={(e) => setEditForm({ ...editForm, effort: parseInt(e.target.value) || 15 })}
                              className="w-20 bg-muted border border-border rounded p-1 text-foreground text-xs"
                            />
                          ) : (
                            <span className="text-xs text-foreground">{m.estimated_effort_minutes} דק׳</span>
                          )}
                        </div>

                        <div>
                          <p className="text-xs text-muted-foreground mb-1">תיאור למטופל:</p>
                          {editingMissionId === m.id ? (
                            <textarea
                              value={editForm.description}
                              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                              className="w-full bg-muted border border-border rounded-lg p-2 text-foreground text-xs h-20 scrollbar-hide"
                            />
                          ) : (
                            <p className="text-xs text-foreground whitespace-pre-line">
                              {m.message_to_patient_draft}
                            </p>
                          )}
                        </div>

                        {m.description_for_therapist && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">תיאור למטפל:</p>
                            <p className="text-xs text-foreground whitespace-pre-line">
                              {m.description_for_therapist}
                            </p>
                          </div>
                        )}

                        {m.safety_flags && m.safety_flags.length > 0 && (
                          <div>
                            <p className="text-[11px] text-red-400 mb-1">דגלי זהירות:</p>
                            <ul className="list-disc pr-4 space-y-1 text-[11px] text-red-300">
                              {m.safety_flags.map((f, idx) => (
                                <li key={idx}>{f}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {m.notes_for_therapist && (
                          <div>
                            <p className="text-[11px] text-muted-foreground mb-1">
                              הערת AI למטפל:
                            </p>
                            <p className="text-[11px] text-foreground whitespace-pre-line">
                              {m.notes_for_therapist}
                            </p>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => approveSuggestedMission(m)}
                          disabled={!m.dayOfWeek}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed text-white text-xs font-bold py-2 rounded-lg transition-all"
                        >
                          {m.dayOfWeek ? `אשר והוסף ליום ${m.dayOfWeek}` : 'בחר יום קודם'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background relative dir-rtl transition-colors duration-300">
      {/* הודעת הצלחה */}
      {showSuccessToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100] animate-slideDown">
          <div className="bg-primary text-primary-foreground px-6 py-3 rounded-xl shadow-2xl flex items-center gap-2">
            <CheckCircle size={20} />
            <span className="font-bold">{successMessage}</span>
          </div>
        </div>
      )}

      <div className="flex-1 p-6 overflow-y-auto pb-24 scrollbar-hide">
        <TherapistAggregateInsights patients={patients} />
        <AIImpactMetrics patients={patients} />

        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="חפש מטופל..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-card border border-border rounded-xl py-3 pr-10 pl-4 text-foreground focus:border-primary focus:outline-none transition-colors duration-300"
            />
            <Search className="absolute top-3 right-3 text-muted-foreground" size={20} />
          </div>
          <button
            onClick={() => setShowAddPatient(true)}
            className="flex bg-primary hover:bg-primary/90 text-primary-foreground px-4 md:px-5 rounded-xl items-center gap-2 text-sm font-bold transition-colors shrink-0"
            aria-label="הוסף מטופל"
          >
            <UserPlus size={18} /> <span className="hidden sm:inline">הוסף מטופל</span>
          </button>
        </div>

        <h3 className="text-foreground font-bold mb-3 flex items-center gap-2">
          <Users size={18} /> רשימת מטופלים ({filteredPatients.length})
        </h3>

        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {loading ? (
            <p className="text-muted-foreground">טוען...</p>
          ) : (
            filteredPatients.map((patient, idx) => {
              const isOnline =
                patient.last_seen &&
                new Date().getTime() - new Date(patient.last_seen).getTime() <
                5 * 60 * 1000;
              return (
                <PatientWindow
                  key={patient.id}
                  index={idx}
                  name={patient.full_name}
                  avatarId={patient.avatar_id}
                  online={!!isOnline}
                  signal={homeSignals[patient.id]}
                  onOpen={() => openPatientDetail(patient)}
                />
              );
            })
          )}
        </div>

        {filteredPatients.length === 0 && !loading && (
          <div className="text-center py-10 opacity-50 border border-dashed border-border rounded-xl">
            <p className="text-muted-foreground">לא נמצאו מטופלים תואמים</p>
          </div>
        )}
      </div>

      {showAddPatient && (
        <div className="absolute inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-sm border border-border rounded-2xl p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                מטופל חדש
              </h3>
              <button
                onClick={() => setShowAddPatient(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreatePatient} className="space-y-4">
              <input
                value={newPatient.name}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, name: e.target.value })
                }
                className="w-full bg-white border border-input rounded-lg p-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="שם מלא"
                required
              />
              <input
                type="number"
                value={newPatient.age}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, age: e.target.value })
                }
                className="w-full bg-white border border-input rounded-lg p-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder="גיל"
                required
              />
              <textarea
                value={newPatient.issue}
                onChange={(e) =>
                  setNewPatient({ ...newPatient, issue: e.target.value })
                }
                className="w-full bg-white border border-input rounded-lg p-3 text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none h-20 resize-none"
                placeholder="תיאור הבעיה"
                required
              />
              <button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-3 rounded-xl mt-2"
              >
                צור משתמש
              </button>
            </form>
          </div>
        </div>
      )}

      {lastCreatedCreds && (
        <div className="absolute inset-0 z-[60] bg-background/90 flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-sm border-2 border-green-500/50 rounded-2xl p-6 shadow-2xl text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 text-green-500">
              <Lock size={32} />
            </div>
            {credsStep === 'show' ? (
              <>
                <h3 className="text-2xl font-bold text-foreground mb-2">המשתמש נוצר!</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  הסיסמה מוצגת רק עכשיו. העתק/י ומסור/י אותה למטופל.
                </p>
                <div
                  className="bg-muted p-4 rounded-xl border border-border mb-4 text-left"
                  dir="ltr"
                >
                  <div className="mb-2">
                    <span className="text-xs text-muted-foreground">USERNAME</span>
                    <div className="text-foreground font-mono text-lg font-bold select-all">
                      {lastCreatedCreds.username}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">PASSWORD</span>
                    <div className="text-foreground font-mono text-lg font-bold select-all">
                      {lastCreatedCreds.password}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={copyCreds}
                  className="w-full flex items-center justify-center gap-2 bg-card border-2 border-primary text-primary hover:bg-primary/5 font-bold py-3 rounded-xl mb-3 transition-colors"
                >
                  {credsCopied ? <Check size={18} /> : <Copy size={18} />}
                  {credsCopied ? 'הועתק ✓' : 'העתק שם משתמש וסיסמה'}
                </button>
                <button
                  type="button"
                  onClick={() => setCredsStep('confirm')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl"
                >
                  סיימתי להעתיק
                </button>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-foreground mb-2">בטוח שהעתקת?</h3>
                <p className="text-sm text-foreground mb-6">
                  אחרי הסגירה אי אפשר לראות את הסיסמה שוב, ולא תהיה גישה למטופל.
                  {!credsCopied && (
                    <span className="mt-2 block font-bold text-accent">
                      שים/י לב: לא לחצת על "העתק".
                    </span>
                  )}
                </p>
                <button
                  type="button"
                  autoFocus
                  onClick={() => setCredsStep('show')}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-3 rounded-xl mb-3"
                >
                  חזרה לפרטים
                </button>
                <button
                  type="button"
                  onClick={closeCreds}
                  className="w-full bg-card border border-border text-foreground hover:bg-muted font-bold py-3 rounded-xl"
                >
                  כן, העתקתי וסגור
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TherapistDashboard;